import { Link } from 'react-router';
import { isOrgAdmin } from '../components/Layout';
import { Card, CodeBlock, CopyField, PageHeader, Section } from '../components/ui';
import { useMe, usePlants } from '../lib/queries';

/** How to get data in (edge devices) and out (API and API keys now, MCP next). */
export function Connect() {
  const origin = window.location.origin;
  const me = useMe();
  const plants = usePlants();
  const admin = !!me.data && isOrgAdmin(me.data);
  const slug = plants.data?.[0]?.slug ?? 'dev-01';
  const python = `import os
import requests

response = requests.get(
    "${origin}/v1/plants/${slug}/latest",
    headers={"Authorization": f"Bearer {os.environ['VARDEX_API_KEY']}"},
    timeout=30,
)
response.raise_for_status()
for value in response.json()["data"]:
    print(value["topic"], value["value"], value["time"])`;
  return (
    <>
      <PageHeader title="Connect" subtitle="Get data in from your plants, and out to your own tools" />

      <Section title="Edge devices">
        <Card className="space-y-2 p-4 text-sm">
          <p>Each plant's IPC sends its data with <span className="font-medium">vardex-sync</span>, over outbound HTTPS only. Nothing in the cloud can write to the plant.</p>
          <ol className="list-decimal space-y-1 pl-5">
            <li>Open the plant, go to <span className="font-medium">Devices</span> and add a device (organization admins).</li>
            <li>Run the command it shows on the IPC, as Administrator. The code works once, for 24 hours.</li>
            <li>The plant shows <span className="font-medium">Live</span> within a few minutes. By default it also sends the last 30 days of history.</li>
          </ol>
        </Card>
      </Section>

      <Section title="REST API">
        <Card className="space-y-3 p-4 text-sm">
          <p>Everything this app shows comes from the API: plants, datapoints, latest values, time series with automatic resolution, alarms. Times are UTC, and errors are RFC 9457 problem JSON.</p>
          <div>
            <div className="mb-1 font-medium">Base URL</div>
            <CopyField value={`${origin}/v1`} />
          </div>
          <div>
            <div className="mb-1 font-medium">OpenAPI specification</div>
            <CopyField value={`${origin}/v1/openapi.json`} />
          </div>
        </Card>
      </Section>

      <Section title="API keys for scripts and integrations">
        <Card className="space-y-3 p-4 text-sm">
          <p>
            An API key reads the plants it was created for: plants, datapoints, latest values, history and alarms. It can't change anything.{' '}
            {admin
              ? <>Create one under <Link to="/api-keys" className="text-accent underline">API keys</Link>.</>
              : 'Ask an admin of your organization for one.'}{' '}
            Keep it in your integration's secret store, and send it in the <code>Authorization</code> header.
          </p>
          <div>
            <div className="mb-1 font-medium">curl</div>
            <CopyField value={`curl -H "Authorization: Bearer $VARDEX_API_KEY" ${origin}/v1/plants`} />
          </div>
          <div>
            <div className="mb-1 font-medium">Python</div>
            <CodeBlock code={python} />
          </div>
          <p className="text-muted">Each key may make 600 requests a minute. Past that the API answers 429; wait a minute and try again.</p>
        </Card>
      </Section>

      <Section title="Claude and other AI tools (MCP)">
        <Card className="p-4 text-sm">
          <p>Coming soon: add Vardex to Claude with one URL, sign in with Microsoft or Google, and ask questions about your plants in plain language. It will see exactly what you see here.</p>
        </Card>
      </Section>
    </>
  );
}
